import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition, yieldToMainThread } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    const templateCard = this.querySelector('[ref="cardGallery"]');

    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    if (!viewMorePrevious && !viewMoreNext) return;

    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          if (viewTransition.current) {
            await viewTransition.current;
          }

          for (const entry of entries) {
            if (entry.isIntersecting) {
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

#showLoader() {
  const loader = this.querySelector('[data-infinite-loader]');

  if (!loader) return;

  loader.classList.add('is-loading');
  loader.setAttribute('aria-hidden', 'false');
}

#hideLoader() {
  const loader = this.querySelector('[data-infinite-loader]');

  if (!loader) return;

  loader.classList.remove('is-loading');
  loader.setAttribute('aria-hidden', 'true');
}

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo
   * @returns {boolean}
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) {
      return false;
    }

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber
   * @param {URL} [url]
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = {
      page: pageNumber,
      url,
    };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';

      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const pageContent = await sectionRenderer.getSectionHTML(
      this.sectionId,
      true,
      pageInfo.url
    );

    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
  const { grid } = this.refs;

  if (!grid) return;

  const nextPage = this.#getPage('next');

  if (!nextPage || !this.#shouldUsePage(nextPage)) {
    this.#hideLoader();
    return;
  }

  // Show loader immediately
  this.#showLoader();

  try {
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    /*
     * If the next page has not already been fetched,
     * fetch it now and wait for it.
     */
    if (!nextPageItemElements) {
      const promise = new Promise((resolve) => {
        this.#resolveNextPagePromise = resolve;
      });

      this.#fetchPage('next');

      await promise;

      nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        return;
      }
    }

    /*
     * Add the new products
     */
    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    await yieldToMainThread();

    /*
     * Update browser URL
     */
    history.pushState('', '', nextPage.url.toString());

    /*
     * Hide loader AFTER products have been inserted
     */
    this.#hideLoader();

    /*
     * Pre-fetch the following page in the background.
     * The loader does NOT show during this background request.
     */
    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

  } catch (error) {
    console.error('Infinite scroll error:', error);

    this.#hideLoader();
  }
}

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');

    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);

    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      this.#fetchPage('previous');

      await promise;

      previousPageItemElements = this.#getGridForPage(previousPage.page);

      if (!previousPageItemElements) return;
    }

    const currentScrollTop = getScrollTop();
    const firstElement = grid.firstElementChild;

    const oldHeight = firstElement
      ? firstElement.getBoundingClientRect().top + currentScrollTop
      : 0;

    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    if (firstElement) {
      const newHeight =
        firstElement.getBoundingClientRect().top + getScrollTop();

      const heightDiff = newHeight - oldHeight;

      scrollTo({
        top: currentScrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    await yieldToMainThread();

    history.pushState('', '', previousPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;

    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);

    const page = isPrevious
      ? currentCardPage - 1
      : currentCardPage + 1;

    const url = new URL(window.location.href);

    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(
      pageHTML,
      'text/html'
    );

    const gridElement = parsedPage.querySelector('[ref="grid"]');

    if (!gridElement) return;

    return gridElement.querySelectorAll(
      ':scope > [ref="cards[]"]'
    );
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) {
      throw new Error('The section-id attribute is required');
    }

    return id;
  }

  #handleFilterUpdate = (event) => {
    const eventSection =
      event.target?.closest('[id^="shopify-section-"]');

    if (
      eventSection &&
      eventSection.id !== `shopify-section-${this.sectionId}`
    ) {
      return;
    }

    this.pages.clear();

    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    const currentLastPage =
      this.refs.grid?.dataset.lastPage;

    const observer = new MutationObserver(() => {
      const newLastPage =
        this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        if (!this.isConnected) {
          return;
        }

        this.#observeViewMore();

        this.#fetchPage('next');
      }
    });

    const { grid } = this.refs;

    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true,
      });

      setTimeout(() => {
        observer.disconnect();
      }, 3000);
    }
  };
}